/**
 * The left rail, as in the prototype: the logo, a study picker, the four numbered setup steps (green
 * once done, amber and pulsing for the next, red when not set), live job progress, then Results.
 */

import { ChevronDown, CreditCard, History, KeyRound, LogOut, Settings, Shield, Square } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";

import { useAuth } from "@/auth/AuthProvider";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useBranding } from "@/lib/useBranding";
import { type JobView, useCancelJob, useJobs, useMe, useRuns, useStudies, useStudy } from "@/lib/queries";
import { PasswordFields, passwordProblem } from "@/pages/PasswordFields";

import { HistoryDialog, SettingsDialog } from "./StudyDialogs";

import { useDraft } from "./draft";
import { PlayResults } from "./PlayResults";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { nextStep, stateOf, stepsFor, type Step, type StepState } from "./steps";

export type DialogName = "audience" | "message" | "promises" | "ask";

const BUTTONS: Record<Step["number"], { text: string; dialog: DialogName }> = {
  1: { text: "Who reacts…", dialog: "audience" },
  2: { text: "The message…", dialog: "message" },
  3: { text: "Promises to keep…", dialog: "promises" },
  4: { text: "Ask the community", dialog: "ask" },
};

export function Rail({ onOpen }: { onOpen: (dialog: DialogName) => void }) {
  const { studyId } = useParams();
  const navigate = useNavigate();
  const { brand } = useBranding();
  const { draft } = useDraft();
  const study = useStudy(studyId).data;
  const runs = useRuns(studyId).data ?? [];
  const jobs = useJobs(studyId).data ?? [];
  const steps = stepsFor(study, draft, runs);
  const next = nextStep(steps);
  const active = jobs.filter((j) => j.status === "queued" || j.status === "running");

  return (
    <aside className="flex w-full flex-col md:h-dvh gap-4 overflow-y-auto border-r border-white/8 bg-[#16181d] px-4 pt-3 pb-4 md:w-[300px] md:shrink-0">
      <img src={brand.logo_url} alt={brand.product_name} className="mt-1 block h-14 w-auto max-w-full self-start" />
      <StudyPicker current={studyId} onPick={(id) => navigate(id ? `/studies/${id}` : "/")} />

      <nav aria-label="Set up" className="space-y-3">
        {steps.map((step) => {
          const state = stateOf(step, next);
          const button = BUTTONS[step.number];
          const blocked =
            (step.number === 1 && Boolean(study)) || // a study's audience is fixed once it exists
            (step.number === 3 && !steps[1].value) ||
            (step.number === 4 && (!steps[0].value || !steps[1].value || active.length > 0));
          return (
            <div key={step.number} className="space-y-1.5">
              <StepCaption step={step} state={state} />
              <Button
                variant={step.number === 4 ? "default" : "outline"}
                className={cn("w-full", state === "next" && step.number !== 4 && "border-step-next/70")}
                disabled={blocked}
                onClick={() => onOpen(button.dialog)}
              >
                {button.text}
              </Button>
            </div>
          );
        })}
        {!steps[0].value || !steps[1].value ? (
          <p className="text-xs text-muted-foreground">Set an audience and a message first.</p>
        ) : null}
      </nav>

      {/* Directly under "Ask the community", because it is about what that button produced. */}
      <PlayResults />

      {active.length > 0 && studyId && <JobProgress studyId={studyId} jobs={active} />}

      {studyId && <StudyTools studyId={studyId} />}

      {studyId && runs.length > 0 && (
        <section aria-label="Results" className="space-y-1.5">
          <Separator className="bg-white/8" />
          <h2 className="pt-1 text-xs font-medium tracking-wide text-muted-foreground uppercase">Results</h2>
          {[...runs].reverse().filter((r) => r.status === "done").map((run) => {
            const variant = (study?.variants ?? []).find((v) => v.variant_id === run.variant_id);
            return (
              <Button key={run.run_id} variant="ghost" className="w-full justify-start"
                      onClick={() => navigate(`/studies/${studyId}/runs/${run.run_id}`)}>
                {variant?.label ?? run.variant_id}
                <span className="ml-auto text-xs text-muted-foreground">
                  {run.created_at ? new Date(run.created_at).toLocaleDateString() : ""}
                </span>
              </Button>
            );
          })}
        </section>
      )}

      <AccountFooter />
    </aside>
  );
}

/** Settings and History for the study being looked at (decisions 6.5 and 6.6). */
function StudyTools({ studyId }: { studyId: string }) {
  const [open, setOpen] = useState<"settings" | "history" | null>(null);
  return (
    <section className="flex gap-1">
      <Button size="xs" variant="ghost" onClick={() => setOpen("settings")}>
        <Settings aria-hidden="true" /> Settings
      </Button>
      <Button size="xs" variant="ghost" onClick={() => setOpen("history")}>
        <History aria-hidden="true" /> History
      </Button>
      <Dialog open={open !== null} onOpenChange={(value) => !value && setOpen(null)}>
        {open === "settings" && <SettingsDialog studyId={studyId} onDone={() => setOpen(null)} />}
        {open === "history" && <HistoryDialog studyId={studyId} onDone={() => setOpen(null)} />}
      </Dialog>
    </section>
  );
}

function StepCaption({ step, state }: { step: Step; state: StepState }) {
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="flex items-center">
        <span
          className={cn(
            "mr-2 inline-flex size-[1.45rem] items-center justify-center rounded-full text-xs font-bold",
            state === "done" && "bg-step-done text-white",
            state === "next" && "animate-step-pulse border-2 border-step-next bg-step-next/18 text-step-next",
            state === "todo" && "border border-step-todo/55 bg-step-todo/16 text-step-todo",
          )}
        >
          {step.number}
        </span>
        <span className={cn(state === "next" ? "font-semibold text-step-next" : "opacity-85")}>{step.label}</span>
      </span>
      {step.value ? (
        <span className="truncate font-semibold text-step-done">✓ {step.value}</span>
      ) : (
        <span className={cn("font-semibold", state === "next" ? "animate-step-fade text-step-next" : "text-step-todo")}>
          not set
        </span>
      )}
    </div>
  );
}

function StudyPicker({ current, onPick }: { current: string | undefined; onPick: (id: string | null) => void }) {
  const studies = useStudies().data ?? [];
  if (!studies.length) return null;
  return (
    <select
      aria-label="Study"
      className="h-9 w-full rounded-md border border-white/10 bg-[#0f1115] px-2 text-sm"
      value={current ?? ""}
      onChange={(e) => onPick(e.target.value || null)}
    >
      <option value="">＋ New study</option>
      {studies.map((s) => (
        <option key={s.study_id} value={s.study_id}>
          {s.name}
          {s.mode === "mock" ? " (mock)" : ""}
        </option>
      ))}
    </select>
  );
}

function JobProgress({ studyId, jobs }: { studyId: string; jobs: JobView[] }) {
  const cancel = useCancelJob(studyId);
  return (
    <section aria-label="Running" aria-live="polite" className="space-y-2 rounded-lg border border-white/10 bg-black/30 p-3">
      {jobs.map((job) => (
        <div key={job.job_id} className="space-y-1.5">
          <div className="flex items-center justify-between gap-2 text-sm">
            <span className="truncate font-medium">{job.title}</span>
            <Button size="xs" variant="ghost" disabled={job.cancel_requested || cancel.isPending}
                    onClick={() => cancel.mutate(job.job_id)} aria-label={`Cancel ${job.title}`}>
              <Square aria-hidden="true" />
              {job.cancel_requested ? "Stopping…" : "Cancel"}
            </Button>
          </div>
          {job.status === "queued" ? (
            <p className="text-xs text-muted-foreground">
              Queued{job.queue_position ? ` - ${job.queue_position} ahead` : " - starting"}
            </p>
          ) : (
            <>
              <Progress value={null} className="h-1" />
              <p className="truncate text-xs text-muted-foreground">{job.stage || "starting…"}</p>
            </>
          )}
        </div>
      ))}
    </section>
  );
}

function AccountFooter() {
  const { state, signOut } = useAuth();
  const me = useMe().data;
  const [choosing, setChoosing] = useState(false);
  const passwords = state.status === "signed-in" && state.methods.includes("password");
  return (
    <footer className="space-y-1 pt-3 text-xs text-muted-foreground md:mt-auto">
      <Separator className="mb-2 bg-white/8" />
      {/* One button instead of six loose things. The rail's job is the four steps; everything about
          the account sits behind this, where it can be found without competing with them. */}
      <DropdownMenu>
        <DropdownMenuTrigger
          className="flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left hover:bg-white/6"
          aria-label="Account"
        >
          <span className="min-w-0 flex-1 truncate">{state.status === "signed-in" ? state.email : ""}</span>
          {me && <span className="shrink-0 text-foreground">${me.credit.available_usd.toFixed(2)}</span>}
          <ChevronDown aria-hidden="true" className="size-3.5 shrink-0" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-56">
          {/* Base UI requires a label to sit in a group; outside one it throws and takes the app
              with it, which is how this was found. */}
          {me && (
            <DropdownMenuGroup>
              <DropdownMenuLabel className="font-normal text-muted-foreground">
                Credit ${me.credit.available_usd.toFixed(2)}
                {me.credit.held_usd > 0 && ` · ${"$" + me.credit.held_usd.toFixed(2)} reserved`}
              </DropdownMenuLabel>
            </DropdownMenuGroup>
          )}
          <DropdownMenuItem render={<Link to="/billing" />}>
            <CreditCard aria-hidden="true" /> Billing and credit
          </DropdownMenuItem>
          {passwords && (
            <DropdownMenuItem onClick={() => setChoosing(true)}>
              <KeyRound aria-hidden="true" /> Set a password
            </DropdownMenuItem>
          )}
          {/* Only an offer, not a gate: the admin endpoints check for themselves and 404. */}
          {me?.is_admin && (
            <DropdownMenuItem render={<Link to="/admin" />}>
              <Shield aria-hidden="true" /> Admin
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem render={<Link to="/examples" />}>Examples</DropdownMenuItem>
          <DropdownMenuItem render={<Link to="/pricing" />}>Pricing</DropdownMenuItem>
          <DropdownMenuItem render={<Link to="/terms" />}>Terms</DropdownMenuItem>
          <DropdownMenuItem render={<Link to="/privacy" />}>Privacy</DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => void signOut()}>
            <LogOut aria-hidden="true" /> Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Dialog open={choosing} onOpenChange={setChoosing}>
        {choosing && <SetPasswordDialog onDone={() => setChoosing(false)} />}
      </Dialog>
    </footer>
  );
}

/** Optional (decision 3.5a): a password to sign in with, as well as email links and Google. */
function SetPasswordDialog({ onDone }: { onDone: () => void }) {
  const { state, setPassword, sendPasswordLink } = useAuth();
  const [password, setValue] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tooOld, setTooOld] = useState(false);
  const email = state.status === "signed-in" ? state.email : "";

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const problem = passwordProblem(password, again);
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    try {
      await setPassword(password);
      toast.success("Password set - you can use it next time you sign in");
      onDone();
    } catch (err) {
      const message = err instanceof Error ? err.message : "the password could not be set";
      setTooOld(message.includes("sign in again"));
      setError(message);
    } finally {
      setBusy(false);
    }
  }

  async function emailLink() {
    setBusy(true);
    try {
      await sendPasswordLink(email);
      toast.success(`We emailed ${email} a link to choose a password`);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "the email could not be sent");
    } finally {
      setBusy(false);
    }
  }

  return (
    <DialogContent className="sm:max-w-sm">
      <form onSubmit={onSubmit} className="space-y-4">
        <DialogHeader>
          <DialogTitle>Set a password</DialogTitle>
          <DialogDescription>
            Sign in with {email} and this password next time. Email links and Google keep working too.
          </DialogDescription>
        </DialogHeader>
        <PasswordFields password={password} again={again} onPassword={setValue} onAgain={setAgain} />
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <DialogFooter>
          {tooOld && (
            <Button type="button" variant="outline" disabled={busy} onClick={() => void emailLink()}>
              Email me a link instead
            </Button>
          )}
          <Button type="submit" disabled={busy}>
            Save password
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
